import type { Raster } from '@repo/warp-engine';
import type { Point2 } from '../geometry/matrix';
import { blendPixel } from './composite';
import { samplePremul } from './sample-premul';

type Triangle = readonly [Point2, Point2, Point2];

const NO_OFFSET = { x: 0, y: 0 } as const;

/** a 가 b 보다 앞(y 먼저, 같으면 x)인가 — 공유 변을 두 삼각형이 같은 방향으로 계산하게 하는 기준. */
const precedes = (a: Point2, b: Point2): boolean => a.y < b.y || (a.y === b.y && a.x < b.x);

/**
 * 변 a→b 의 변 함수 (b − a) × (p − a). 두 끝점을 늘 같은 순서로 계산해 부호만 뒤집으므로,
 * 공유 변을 반대 방향으로 쓰는 이웃 삼각형과 값이 정확히 반대가 된다(겹침·빈틈 없음).
 */
const edge = (a: Point2, b: Point2, px: number, py: number): number => {
  if (precedes(b, a)) return -edge(b, a, px, py);
  return (b.x - a.x) * (py - a.y) - (b.y - a.y) * (px - a.x);
};

/**
 * 변 함수가 0 인 픽셀을 이 변이 가질지 — 넓이가 양수(화면에서 시계 방향)인 삼각형의 top-left 규칙.
 * 위쪽 변(수평이고 오른쪽으로 감)과 왼쪽 변(위로 감)만 포함한다.
 */
const isTopLeft = (a: Point2, b: Point2): boolean => (a.y === b.y && b.x > a.x) || b.y < a.y;

/**
 * src 의 삼각형 srcTri 를 dst 의 삼각형 dstTri 로 아핀 변환해 source-over 로 그린다.
 * dst 픽셀 (x, y) 의 중심은 dstTri 좌표로 (x + 0.5 + offset.x, y + 0.5 + offset.y) 다. 중심이 삼각형 안이거나
 * top-left 변 위면 칠한다(공유 변은 한쪽만). 원본은 transform-raster 와 같은 premultiplied 쌍선형으로 표본한다.
 */
export const drawRasterTriangle = (
  dst: Raster,
  src: Raster,
  srcTri: Triangle,
  dstTri: Triangle,
  offset: { readonly x: number; readonly y: number } = NO_OFFSET,
): void => {
  let [d0, d1, d2] = dstTri;
  let [s0, s1, s2] = srcTri;
  let area = (d1.x - d0.x) * (d2.y - d0.y) - (d1.y - d0.y) * (d2.x - d0.x);
  if (!(Math.abs(area) > 0) || !Number.isFinite(area)) return;
  if (area < 0) {
    [d1, d2] = [d2, d1];
    [s1, s2] = [s2, s1];
    area = -area;
  }
  const include0 = isTopLeft(d1, d2);
  const include1 = isTopLeft(d2, d0);
  const include2 = isTopLeft(d0, d1);

  const x0 = Math.max(0, Math.floor(Math.min(d0.x, d1.x, d2.x) - offset.x));
  const y0 = Math.max(0, Math.floor(Math.min(d0.y, d1.y, d2.y) - offset.y));
  const x1 = Math.min(dst.width, Math.ceil(Math.max(d0.x, d1.x, d2.x) - offset.x));
  const y1 = Math.min(dst.height, Math.ceil(Math.max(d0.y, d1.y, d2.y) - offset.y));

  const p00 = new Float64Array(4);
  const p10 = new Float64Array(4);
  const p01 = new Float64Array(4);
  const p11 = new Float64Array(4);
  for (let y = y0; y < y1; y += 1) {
    const py = y + 0.5 + offset.y;
    for (let x = x0; x < x1; x += 1) {
      const px = x + 0.5 + offset.x;
      const e0 = edge(d1, d2, px, py);
      if (e0 < 0 || (e0 === 0 && !include0)) continue;
      const e1 = edge(d2, d0, px, py);
      if (e1 < 0 || (e1 === 0 && !include1)) continue;
      const e2 = edge(d0, d1, px, py);
      if (e2 < 0 || (e2 === 0 && !include2)) continue;
      const w0 = e0 / area;
      const w1 = e1 / area;
      const w2 = e2 / area;
      const u = w0 * s0.x + w1 * s1.x + w2 * s2.x - 0.5;
      const v = w0 * s0.y + w1 * s1.y + w2 * s2.y - 0.5;
      const ix = Math.floor(u);
      const iy = Math.floor(v);
      const fx = u - ix;
      const fy = v - iy;
      samplePremul(src, ix, iy, p00);
      samplePremul(src, ix + 1, iy, p10);
      samplePremul(src, ix, iy + 1, p01);
      samplePremul(src, ix + 1, iy + 1, p11);
      const mix = (i: number): number =>
        (p00[i] ?? 0) * (1 - fx) * (1 - fy) +
        (p10[i] ?? 0) * fx * (1 - fy) +
        (p01[i] ?? 0) * (1 - fx) * fy +
        (p11[i] ?? 0) * fx * fy;
      const a = mix(3);
      if (a <= 0) continue;
      blendPixel(dst.data, (y * dst.width + x) * 4, mix(0) / a, mix(1) / a, mix(2) / a, a * 255);
    }
  }
};
